import { baseUrl } from "util/APIBaseUrl";
import getRequest from "util/APIHelperGet";
import { makeRequest } from "util/APIHelper";

/**
 * [PRD-2043] Read-only developer billing dashboard.
 *
 * Thin GET wrappers over the monolith `/billing/*` proxy routes, which forward
 * to billing-service. Every route is gated by the `billing.read` permission.
 * List endpoints return billing-service's `{ data, cursor, hasMore }` envelope.
 */

export interface BsPaginated<T> {
  data: T[];
  cursor: string | null;
  hasMore: boolean;
}

export interface BillingListParams {
  customerId?: string;
  status?: string;
  type?: string;
  startDate?: string;
  endDate?: string;
  cursor?: string;
  limit?: number;
  /** [PRD-3765] Charges list only: one row per invoice (its latest attempt). */
  latestOnly?: boolean;
}

const buildQuery = (params: Record<string, string | number | undefined | null>): string => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") sp.set(key, String(value));
  });
  const query = sp.toString();
  return query ? `?${query}` : "";
};

// --- Reports (Overview) ---

export const getBillingDashboardReport = async () => {
  const { payload } = await getRequest(`${baseUrl}/billing/reports/dashboard`);
  return payload;
};

export const getBillingRevenueReport = async (startDate: string, endDate: string) => {
  const { payload } = await getRequest(`${baseUrl}/billing/reports/revenue${buildQuery({ startDate, endDate })}`);
  return payload;
};

export const getBillingDunningReport = async (startDate: string, endDate: string) => {
  const { payload } = await getRequest(`${baseUrl}/billing/reports/dunning${buildQuery({ startDate, endDate })}`);
  return payload;
};

// [PRD-2625] Overview tab — one call returns every widget's metrics.
export const getBillingOverview = async (startDate: string, endDate: string) => {
  const { payload } = await getRequest(`${baseUrl}/billing/reports/overview${buildQuery({ startDate, endDate })}`);
  return payload;
};

// --- Customers ---

export const listBillingCustomers = async (params: BillingListParams = {}) => {
  const { status, cursor, limit } = params;
  const { payload } = await getRequest(`${baseUrl}/billing/customers${buildQuery({ status, cursor, limit })}`);
  return payload;
};

export const getBillingCustomer = async (id: string) => {
  const { payload } = await getRequest(`${baseUrl}/billing/customers/${id}`);
  return payload;
};

// [phase 2] Server-side search over ALL customers (BS admin search via x-admin-role).
export const searchBillingCustomers = async (params: { q?: string; status?: string; cursor?: string; limit?: number } = {}) => {
  const { q, status, cursor, limit } = params;
  const { payload } = await getRequest(`${baseUrl}/billing/customers/search${buildQuery({ q, status, cursor, limit })}`);
  return payload;
};

export const getBillingCustomerPaymentMethods = async (id: string) => {
  const { payload } = await getRequest(`${baseUrl}/billing/customers/${id}/payment-methods`);
  return payload;
};

export const getBillingCustomerCreditBalance = async (id: string) => {
  const { payload } = await getRequest(`${baseUrl}/billing/customers/${id}/credit-balance`);
  return payload;
};

// --- Subscriptions (supports customerId for the customer-detail tab) ---

export const listBillingSubscriptions = async (params: BillingListParams = {}) => {
  const { customerId, status, cursor, limit } = params;
  const { payload } = await getRequest(
    `${baseUrl}/billing/subscriptions${buildQuery({ customerId, status, cursor, limit })}`
  );
  return payload;
};

// --- Invoices (supports customerId for the customer-detail tab) ---

export const listBillingInvoices = async (params: BillingListParams = {}) => {
  const { customerId, status, type, cursor, limit } = params;
  const { payload } = await getRequest(
    `${baseUrl}/billing/invoices${buildQuery({ customerId, status, type, cursor, limit })}`
  );
  return payload;
};

export const getBillingInvoice = async (id: string) => {
  const { payload } = await getRequest(`${baseUrl}/billing/invoices/${id}`);
  return payload;
};

// --- [bucket-B] Global charges + refunds lists ---

export const listBillingCharges = async (params: BillingListParams = {}) => {
  const { customerId, status, startDate, endDate, cursor, limit, latestOnly } = params;
  const { payload } = await getRequest(
    `${baseUrl}/billing/charges${buildQuery({
      customerId,
      status,
      startDate,
      endDate,
      cursor,
      limit,
      latestOnly: latestOnly ? "true" : undefined,
    })}`
  );
  return payload;
};

// [PRD-2043] Day/status charge rollup for the Charge Report summary table (returns an array).
export const getBillingChargeSummary = async (
  params: { startDate?: string; endDate?: string; status?: string } = {}
) => {
  const { startDate, endDate, status } = params;
  const { payload } = await getRequest(
    `${baseUrl}/billing/charges/summary${buildQuery({ startDate, endDate, status })}`
  );
  return payload;
};

export const listBillingRefunds = async (params: BillingListParams = {}) => {
  const { customerId, status, cursor, limit } = params;
  const { payload } = await getRequest(`${baseUrl}/billing/refunds${buildQuery({ customerId, status, cursor, limit })}`);
  return payload;
};

// --- [PRD-2043] Expected Funds (collections forecast) ---

export const getExpectedFunds = async (params: { lookBack?: number; lookAhead?: number } = {}) => {
  const { lookBack, lookAhead } = params;
  const { payload } = await getRequest(`${baseUrl}/billing/expected-funds${buildQuery({ lookBack, lookAhead })}`);
  return payload;
};

export const getExpectedFundsCharges = async (date: string) => {
  const { payload } = await getRequest(`${baseUrl}/billing/expected-funds/charges${buildQuery({ date })}`);
  return payload;
};

// --- [PRD-2043] Write: pause/resume a subscription (requires billing.manage) ---
export const updateBillingSubscriptionStatus = async (
  id: string,
  body: { status: "active" | "paused"; note: string }
) => {
  const result = await makeRequest({
    method: "PUT",
    path: `${baseUrl}/billing/subscriptions/${id}/status`,
    contentType: "application/json",
    body: JSON.stringify(body),
  });
  // [PRD-3348] makeRequest resolves (never rejects) on HTTP errors and returns
  // a network failure as a value, so surface both as a thrown error. A 409/422
  // from billing-service comes back as { success: false, message }; the
  // message rides on error.response.data for the caller to show.
  if (result instanceof Error) throw result;
  const { ok, status, payload } = result || {};
  if (ok === false || payload?.success === false) {
    const message = typeof payload?.message === "string" && payload.message ? payload.message : "Request failed";
    throw Object.assign(new Error(message), { response: { status, data: payload } });
  }
  return payload;
};

// [PRD-3443] Write: manually retry a failed charge (requires billing.manage).
export const retryBillingInvoiceCharge = async (invoiceId: string) => {
  const result = await makeRequest({
    method: "POST",
    path: `${baseUrl}/billing/invoices/${invoiceId}/retry`,
    contentType: "application/json",
  });
  // makeRequest resolves (never rejects) on HTTP errors and returns a network
  // failure as a value, so surface both as a thrown error. A 409/422 from
  // billing-service (e.g. already has a pending charge) comes back as
  // { success: false, message }; the message rides on error.response.data.
  if (result instanceof Error) throw result;
  const { ok, status, payload } = result || {};
  if (ok === false || payload?.success === false) {
    const message = typeof payload?.message === "string" && payload.message ? payload.message : "Request failed";
    throw Object.assign(new Error(message), { response: { status, data: payload } });
  }
  return payload;
};

// --- [phase 2] Admin-gated reads (monolith proxy sends x-admin-role: admin) ---

export interface BillingAdminParams {
  disputeStatus?: string;
  entityType?: string;
  entityId?: string;
  dateFrom?: string;
  dateTo?: string;
  cursor?: string;
  limit?: number;
}

export const getBillingCustomerPayments = async (id: string, params: { cursor?: string; limit?: number } = {}) => {
  const { payload } = await getRequest(`${baseUrl}/billing/customers/${id}/payments${buildQuery({ ...params })}`);
  return payload;
};

export const getBillingCustomerDunning = async (id: string, params: { cursor?: string; limit?: number } = {}) => {
  const { payload } = await getRequest(`${baseUrl}/billing/customers/${id}/dunning-history${buildQuery({ ...params })}`);
  return payload;
};

export const listBillingDiscrepancies = async (params: BillingAdminParams = {}) => {
  const { disputeStatus, cursor, limit } = params;
  const { payload } = await getRequest(
    `${baseUrl}/billing/reconciliation/discrepancies${buildQuery({ disputeStatus, cursor, limit })}`
  );
  return payload;
};

export const listBillingAuditTrail = async (params: BillingAdminParams = {}) => {
  const { entityType, entityId, dateFrom, dateTo, cursor, limit } = params;
  const { payload } = await getRequest(
    `${baseUrl}/billing/audit-trail${buildQuery({ entityType, entityId, dateFrom, dateTo, cursor, limit })}`
  );
  return payload;
};

// --- [PRD-3321] Manual charge approval gate ---

export const listBillingChargeApprovals = async (
  params: { status?: string; cursor?: string; limit?: number; date?: string } = {}
) => {
  const { status, cursor, limit, date } = params;
  const { payload } = await getRequest(
    `${baseUrl}/billing/charge-approvals${buildQuery({ status, cursor, limit, date })}`
  );
  return payload;
};

export const getBillingChargeApproval = async (id: string) => {
  const { payload } = await getRequest(`${baseUrl}/billing/charge-approvals/${id}`);
  return payload;
};

// makeRequest() resolves even on a non-2xx response (it never rejects except
// for its own internal 401/403 handling) — callers must check `ok` themselves
// and throw, or a failed request silently looks like success. Throws the same
// `error` array shape the rest of the app already expects (`error[0]?.description`).
const DEFAULT_REQUEST_ERROR = [{ description: "Something went wrong, please try again" }];

// Write: requires billing.manage (same permission as pause/resume subscription).
export const approveBillingChargeApproval = async (id: string) => {
  const result = await makeRequest({
    method: "POST",
    path: `${baseUrl}/billing/charge-approvals/${id}/approve`,
    contentType: "application/json",
    body: JSON.stringify({}),
  });
  if (!result.ok) {
    throw result.payload?.error ?? DEFAULT_REQUEST_ERROR;
  }
  return result.payload;
};

export const rejectBillingChargeApproval = async (id: string, reason?: string) => {
  const result = await makeRequest({
    method: "POST",
    path: `${baseUrl}/billing/charge-approvals/${id}/reject`,
    contentType: "application/json",
    body: JSON.stringify(reason ? { reason } : {}),
  });
  if (!result.ok) {
    throw result.payload?.error ?? DEFAULT_REQUEST_ERROR;
  }
  return result.payload;
};

// All pending approvals, regardless of the list's current status filter —
// for finance's offline review before they reject exceptions and bulk-approve
// the rest. `true`/`"export"` tell makeRequest to read the response as a Blob.
export const exportBillingChargeApprovalsCsv = async (): Promise<Blob> => {
  const result = await getRequest(`${baseUrl}/billing/charge-approvals/export`, true, "export");
  if (!result.ok) {
    throw DEFAULT_REQUEST_ERROR;
  }
  return result.payload;
};

export interface BulkApproveChargeApprovalsResult {
  date: string;
  approvedCount: number;
  skippedGuardCount: number;
  failed: { id: string; reason: string }[];
  cursor: string | null;
  hasMore: boolean;
}

// Approves still-pending charges queued on `date`, one page at a time (so a
// production-sized day doesn't time out a single request), skipping any with
// an active recurring-charge guard warning (left pending for individual
// review). Callers page through by re-passing the returned `cursor` until
// `hasMore` is false.
export const bulkApproveBillingChargeApprovals = async (
  date: string,
  cursor?: string,
  limit?: number
): Promise<BulkApproveChargeApprovalsResult> => {
  const result = await makeRequest({
    method: "POST",
    path: `${baseUrl}/billing/charge-approvals/bulk-approve`,
    contentType: "application/json",
    body: JSON.stringify({ date, ...(cursor ? { cursor } : {}), ...(limit ? { limit } : {}) }),
  });
  if (!result.ok) {
    throw result.payload?.error ?? DEFAULT_REQUEST_ERROR;
  }
  return result.payload;
};
